import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckboxFilter } from '../components/CheckboxFilter';
import { PageHeader } from '../components/PageHeader';
import { Pill } from '../components/Pill';
import { ResultSummary } from '../components/ResultSummary';
import { EmptyPanel, ErrorPanel, LoadingPanel } from '../components/StatePanel';
import { TruncationNotice } from '../components/TruncationNotice';
import { useExclusions, useResearch, useTaxonomy } from '../hooks/useRadarQuery';
import { label } from '../lib/format';

/**
 * The two open states of the review, side by side.
 *
 * A lead is a hypothesis that a company exists and belongs here; an exclusion is
 * the judgement that one does not. Showing only the first left the registry
 * unable to say why something is absent — the protocol requires exclusions to be
 * recorded with a reason and a reconsideration trigger, and until they were,
 * every sweep re-derived the same rejections from the same sources.
 */
export function ResearchPage() {
  // A set, like every other enum filter: "out of scope or not a company" is
  // one question about the ledger rather than two passes over it.
  const [reason, setReason] = useState<string[]>([]);
  const query = useResearch();
  const exclusions = useExclusions(reason);
  const taxonomy = useTaxonomy();

  if (query.isLoading || taxonomy.isLoading) return <LoadingPanel />;
  if (query.isError || taxonomy.isError || !query.data || !taxonomy.data) {
    return <ErrorPanel error={query.error ?? taxonomy.error} />;
  }
  const ruledOut = exclusions.data?.items ?? [];
  return (
    <>
      <PageHeader eyebrow="Weak-signal research" title="Stealth watch" description="Leads remain evidence-graded research objects until independent signals justify promotion into the verified company registry." />
      <TruncationNotice shown={query.data.items.length} total={query.data.total} noun="leads" />
      <section className="card-grid">
        {query.data.items.length ? query.data.items.map((lead) => (
          <article className="card lead-card" key={lead.id}>
            <div className="pill-row"><Pill value={`grade ${lead.evidenceGrade}`} tone={lead.evidenceGrade === 'A' || lead.evidenceGrade === 'B' ? 'positive' : 'warning'} /><Pill value={lead.status} /></div>
            <div><h3>{lead.nameHint}</h3><p>{lead.thesis}</p></div>
            <dl>
              <div><dt>Suspected domain</dt><dd>{label(lead.suspectedDomain)}</dd></div>
              <div><dt>Next action</dt><dd>{lead.nextAction || 'Research further'}</dd></div>
              <div><dt>In-universe likelihood</dt><dd>{Math.round(lead.inUniverseLikelihood * 100)}%</dd></div>
              {/* The trail from a discovered name to the record it became.
                  `promotedCompanyId` was declared in the first migration and
                  written by nothing, so nine promoted leads pointed at nothing
                  and the lead read as a dead end. Shown only when the decision
                  was actually taken — a promoted lead with no link is a defect
                  worth seeing rather than hiding behind a placeholder. */}
              {lead.status === 'promoted' && (
                <div>
                  <dt>Became</dt>
                  <dd>
                    {lead.promotedCompanyId
                      ? <Link to={`/companies/${lead.promotedCompanyId}`}>{lead.nameHint}</Link>
                      : <span className="muted">promoted, but no company recorded</span>}
                  </dd>
                </div>
              )}
            </dl>
          </article>
        )) : <EmptyPanel message="No active stealth leads." />}
      </section>

      <section className="panel section-gap">
        <div className="panel__heading">
          <div><span className="eyebrow">Considered and ruled out</span><h2>Exclusion ledger</h2></div>
          <CheckboxFilter legend="Reason" emptyLabel="All reasons" options={taxonomy.data.exclusionReasons} selected={reason} onChange={setReason} />
        </div>
        {exclusions.isError && <ErrorPanel error={exclusions.error} />}
        {!exclusions.isError && (
          <>
            <ResultSummary
              count={exclusions.data?.total ?? 0}
              one="candidate ruled out."
              many="candidates ruled out."
            />
            {ruledOut.length ? ruledOut.map((entry) => (
              <article className="event-row" key={entry.id}>
                <div className="event-row__marks">
                  <Pill value={entry.reasonCode} tone="accent" />
                  <Pill value={entry.status} tone={entry.status === 'excluded' ? 'neutral' : 'warning'} />
                </div>
                <div className="event-row__body">
                  <strong>{entry.nameHint}</strong>
                  <span>{entry.rationale}</span>
                  {/* The trigger is what makes this a decision rather than a
                      deletion, so it is shown rather than stored and forgotten. */}
                  <small className="muted">Reopens if: {entry.reconsiderationTrigger}</small>
                </div>
                <time>{entry.reviewedAt.slice(0, 10)}</time>
              </article>
            )) : <EmptyPanel message="Nothing has been ruled out under this reason yet." />}
          </>
        )}
      </section>
    </>
  );
}
